import { DEFAULT_LOCALE, type Locale } from "./i18n";

export const CONTENT_KINDS = ["blog", "news", "case", "industry", "integration"] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number];

export const CONTENT_KIND_LABELS: Record<ContentKind, string> = {
  blog: "Blog",
  news: "News",
  case: "Case study",
  industry: "Industry",
  integration: "Integration",
};

/** Public URL segment for each content kind. */
export const CONTENT_KIND_BASE: Record<ContentKind, string> = {
  blog: "/blog",
  news: "/news",
  case: "/cases",
  industry: "/industries",
  integration: "/integrations",
};

export const TRANSLATION_STATUSES = ["missing", "auto", "edited", "source"] as const;
export type TranslationStatus = (typeof TRANSLATION_STATUSES)[number];

export type ContentMetric = { value: string; label: string };

export type ContentTranslation = {
  locale: string;
  slug: string | null;
  title: string;
  excerpt: string;
  body: string;
  seo_title: string | null;
  seo_description: string | null;
  translation_status: TranslationStatus;
};

export type GalleryImage = { url: string; caption?: string };

export type ContentItem = {
  id: string;
  kind: ContentKind;
  slug: string;
  cover_url: string | null;
  video_url?: string | null | undefined;
  gallery?: GalleryImage[] | undefined;
  published: boolean;
  published_at: string | null;
  sort_order: number;
  metrics: ContentMetric[];
  updated_at: string;
};

export type LocalePath = {
  locale: string;
  slug: string;
};

/** An item flattened to a single locale, ready for rendering. */
export type LocalizedContent = ContentItem & {
  locale: string;
  localizedSlug: string;
  localePaths: LocalePath[];
  title: string;
  excerpt: string;
  body: string;
  seoTitle: string;
  seoDescription: string;
  /** true when the requested locale had no usable translation and English was used. */
  fallback: boolean;
};

export type PageBlock = {
  id: string;
  page_slug: string;
  block_key: string;
  sort_order: number;
  data: Record<string, unknown>;
};

export type ContactRequestKind = "trial" | "demo" | "contact" | "partner";
export const REQUEST_STATUSES = ["new", "contacted", "won", "archived"] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export type ContactRequest = {
  id: string;
  kind: ContactRequestKind;
  full_name: string;
  email: string;
  company: string | null;
  phone: string | null;
  country: string | null;
  industry: string | null;
  message: string | null;
  locale: string;
  status: RequestStatus;
  created_at: string;
};

/** Choose the best translation for a locale, falling back to English. */
export function pickTranslation(
  translations: ContentTranslation[],
  locale: Locale | string,
): { translation: ContentTranslation | null; fallback: boolean } {
  const wanted = translations.find((t) => t.locale === locale && t.title.trim() !== "");
  if (wanted) return { translation: wanted, fallback: false };
  const english = translations.find(
    (t) => t.locale === DEFAULT_LOCALE && t.title.trim() !== "",
  );
  if (english) return { translation: english, fallback: true };
  return { translation: translations[0] ?? null, fallback: true };
}

export function translationSlug(translation: ContentTranslation | null | undefined, fallback: string) {
  return translation?.slug?.trim() || fallback;
}

const MONTH_LABELS: Record<Locale, string[]> = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  de: ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."],
  fr: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
  it: ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"],
  pl: ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"],
  uk: ["січ.", "лют.", "бер.", "квіт.", "трав.", "черв.", "лип.", "серп.", "вер.", "жовт.", "лист.", "груд."],
  ar: ["ينا", "فبر", "مار", "أبر", "ماي", "يون", "يول", "أغس", "سبت", "أكت", "نوف", "ديس"],
  hi: ["जन", "फ़र", "मार्च", "अप्रै", "मई", "जून", "जुल", "अग", "सित", "अक्टू", "नवं", "दिस"],
  uz: ["yan.", "fev.", "mar.", "apr.", "may", "iyun", "iyul", "avg.", "sen.", "okt.", "noy.", "dek."],
  th: ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."],
  ur: ["جنوری", "فروری", "مارچ", "اپریل", "مئی", "جون", "جولائی", "اگست", "ستمبر", "اکتوبر", "نومبر", "دسمبر"],
};

export function formatDate(value: string | null, locale: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localeKey = locale in MONTH_LABELS ? (locale as Locale) : DEFAULT_LOCALE;
  const month = MONTH_LABELS[localeKey][date.getUTCMonth()] ?? MONTH_LABELS.en[date.getUTCMonth()];
  return `${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
}

/** A product (platform module or add-on) linked to an industry, with the pain it closes. */
export type IndustryProduct = {
  id: string;
  product_type: "module" | "addon";
  /** module slug (e.g. "production") or add-on slug */
  product_ref: string;
  /** Display name resolved from the module catalog or the add-on record. */
  name: string;
  /** Public link to the product page. */
  href: string;
  pain: string;
  outcome: string;
  sort_order: number;
};
